import { faExclamationTriangle, faFileCsv, faFilePdf, faPlayCircle, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, ButtonToolbar, FormSelect, Pagination } from "react-bootstrap";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import FormGroup from "react-bootstrap/FormGroup";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Table from "react-bootstrap/Table";
import Tooltip from "react-bootstrap/Tooltip";
import { FormattedDate, FormattedMessage, FormattedTime, injectIntl } from "react-intl";
import { SENT_REPORT_STATUS, TEMPORARY_USER } from "../../../common/constant";
import { RespondentsOverviewModel as roModel } from "../../../common/entities";
import rest from "../../../common/rest";
import { createBackendErrorMessages } from "../../../ducks/message";
import { FILTERS, LIST } from "../../../ducks/respondentsOverview";
import { getFullYearFromString } from "../../../utils/ObjectUtils";
import { renderOptions } from "../../../utils/OptionsUtils";
import SurveyUtils from "../../../utils/SurveyUtils";
import StandardView from "../../common/StandardView";
import TrashButton from "../../common/TrashButton";
import FundingAreaContainer from "../fundingArea/FundingAreaContainer";
import LockInfoDialog from "../respondentsOverview/LockInfoDialog";
import DownloadPDFReports from "./DownloadPDFReports";
import RespondentActivationContainer from "./RespondentActivationContainer";


const REPORT_STATUS_BUTTON_ROLES = [
    "program_responsible",
    "program_admin",
    "admin",
    "program_assistant"
]

const PDF_REPORT_LABEL_KEY = "respondents.overview.button.pdf.reports";
const REPORT_STATUS_LABEL_KEY = "respondents.overview.button.report.status";

const OverviewTable = React.memo(function OverviewTable({ rows,
    rowRenderer,
    showLecturer,
    sortTableTemplateBy,
    sortTableTemplateAscending,
    onSortColumn }) {
    return (
        <Table className="mt-4" striped hover responsive>
            <thead>
                <tr>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.program,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.program)}>
                            <FormattedMessage id="respondents.overview.program" />
                            {SurveyUtils.sortIcon(roModel.program, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.title,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.title)}>
                            <FormattedMessage id="respondents.overview.templateTitle" />
                            {SurveyUtils.sortIcon(roModel.title, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.projectForeignId,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.projectForeignId)}>
                            <FormattedMessage id="respondents.overview.project.id" />
                            {SurveyUtils.sortIcon(roModel.projectForeignId, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.universityName,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.universityName)}>
                            <FormattedMessage id="respondents.overview.universityName" />
                            {SurveyUtils.sortIcon(roModel.universityName, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.project,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.project)}>
                            <FormattedMessage id="respondents.overview.project" />
                            {SurveyUtils.sortIcon(roModel.project, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    {showLecturer && (
                        <th
                            className={SurveyUtils.addClassForTableHeader(
                                roModel.lecturer,
                                sortTableTemplateBy,
                                sortTableTemplateAscending
                            )}
                        >
                            <Button variant="link" onClick={() => onSortColumn(roModel.lecturer)}>
                                <FormattedMessage id="respondents.overview.lecturer" />
                                {SurveyUtils.sortIcon(roModel.lecturer, sortTableTemplateBy, sortTableTemplateAscending)}
                            </Button>
                        </th>
                    )}
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.start,
                            sortTableTemplateBy,
                            sortTableTemplateAscending,
                            "date"
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.start)}>
                            <FormattedMessage id="respondents.overview.reportPeriod" />
                            {SurveyUtils.sortIcon(roModel.start, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.status,
                            sortTableTemplateBy,
                            sortTableTemplateAscending
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.status)}>
                            <FormattedMessage id="respondents.overview.status" />
                            {SurveyUtils.sortIcon(roModel.status, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th
                        className={SurveyUtils.addClassForTableHeader(
                            roModel.lastEdit,
                            sortTableTemplateBy,
                            sortTableTemplateAscending,
                            "date"
                        )}
                    >
                        <Button variant="link" onClick={() => onSortColumn(roModel.lastEdit)}>
                            <FormattedMessage id="respondents.overview.lastEdit" />
                            {SurveyUtils.sortIcon(roModel.lastEdit, sortTableTemplateBy, sortTableTemplateAscending)}
                        </Button>
                    </th>
                    <th className="actions">
                        <FormattedMessage id="app.common.title.actions" />
                    </th>
                </tr>
            </thead>
            <tbody>{rows.map(rowRenderer)}</tbody>
        </Table>
    );
});



/**
* The component displays survey templates from the server.
* When compoment was rendered, we will call server to load data.
*/
class RespondentsOverview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            actions: [],
            showReportActivationModal: false,
            projectRefId: null,
            showDownloadPDFDialog: false,
            currentPage: 1,
            pageSize: 100,
            searchQuery: props.filters?.searchQuery || "",
        };
        // Pre-bind the row renderer (avoids rebind per render)
        this.row = this.row.bind(this);
        this.loadOverviewListFilter = this.loadOverviewListFilter.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        // Debounce filter updates (avoid full re-render per keystroke)
        this.updateFilter = debounce((filter) => {
            this.props.overviewPropertyChange(FILTERS, filter);
            this.setState({ currentPage: 1 });
        }, 150);
        this.downloadReporting = this.downloadReporting.bind(this);
        this.downloadPdfReport = this.downloadPdfReport.bind(this);
    }

    handleSearchChange(event) {
        const value = event.target.value;
        const { filters, overviewPropertyChange } = this.props;
        this.setState({
            searchQuery: value,
            currentPage: 1 // ✅ Always go to first page on new search
        });
        overviewPropertyChange(FILTERS, {
            ...filters,
            searchQuery: value
        });
    }

    getFilteredRows(list, filter) {
        if (this._cache && this._cache.list === list && JSON.stringify(this._cache.filter) === JSON.stringify(filter)) {
            return this._cache.result;
        }
        const result = list.filter(row => this.isValidRowToShow(row));
        this._cache = { list, filter, result };
        return result;
    }

    getPagedRows(rows) {
        const { currentPage, pageSize } = this.state;
        const startIndex = (currentPage - 1) * pageSize;
        return rows.slice(startIndex, startIndex + pageSize);
    }

    goToPage(page) {
        const { filters } = this.props;
        window.scrollTo({ top: 0, behavior: "smooth" });
        const totalPages = Math.ceil(
            this.getFilteredRows(this.props.list, filters).length / this.state.pageSize
        );
        if (page >= 1 && page <= totalPages) {
            this.setState({ currentPage: page });
        }
    }

    applySearch(rows) {
        const { searchQuery } = this.state;
        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;

        // Define which fields should be searchable
        const searchFields = [
        roModel.program,
        roModel.title,
        roModel.projectForeignId,
        roModel.lecturer,
        roModel.universityName,
        roModel.project,
        roModel.status
        ];

        return rows.filter((row) =>
        searchFields.some((key) => {
            const val = row[key];
            return val && String(val).toLowerCase().includes(q);
        })
        );
    }

    renderPaginationItems(totalItems) {
        const { currentPage, pageSize } = this.state;
        const totalPages = Math.ceil(totalItems / pageSize);
        const items = [];

        // --- CASE 1: Small number of pages — show all
        if (totalPages <= 9) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => this.goToPage(i)}
                        aria-current={i === currentPage ? "page" : undefined}
                    >
                        {i}
                    </Pagination.Item>
                );
            }
            return items;
        }

        // --- CASE 2: Current page near the start (<=5)
        if (currentPage <= 5) {
            for (let i = 1; i <= 6; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => this.goToPage(i)}
                        aria-current={i === currentPage ? "page" : undefined}
                    >
                        {i}
                    </Pagination.Item>
                );
            }

            items.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);

            // Last 3 pages
            for (let i = totalPages - 2; i <= totalPages; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => this.goToPage(i)}
                        aria-current={i === currentPage ? "page" : undefined}
                    >
                        {i}
                    </Pagination.Item>
                );
            }

            return items;
        }

        // --- CASE 3: Current page near the end (>= totalPages - 4)
        if (currentPage >= totalPages - 4) {
            // First 3 pages
            for (let i = 1; i <= 3; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => this.goToPage(i)}
                        aria-current={i === currentPage ? "page" : undefined}
                    >
                        {i}
                    </Pagination.Item>
                );
            }

            items.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);

            // Last 6 pages
            for (let i = totalPages - 5; i <= totalPages; i++) {
                items.push(
                    <Pagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => this.goToPage(i)}
                        aria-current={i === currentPage ? "page" : undefined}
                    >
                        {i}
                    </Pagination.Item>
                );
            }

            return items;
        }

        // --- CASE 4: Current page in the middle
        // First 3 pages
        for (let i = 1; i <= 3; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => this.goToPage(i)}
                    aria-current={i === currentPage ? "page" : undefined}
                >
                    {i}
                </Pagination.Item>
            );
        }

        items.push(<Pagination.Ellipsis key="middle-left" disabled />);

        // Show the current page ±1
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => this.goToPage(i)}
                    aria-current={i === currentPage ? "page" : undefined}
                >
                    {i}
                </Pagination.Item>
            );
        }

        items.push(<Pagination.Ellipsis key="middle-right" disabled />);

        // Last 3 pages
        for (let i = totalPages - 2; i <= totalPages; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => this.goToPage(i)}
                    aria-current={i === currentPage ? "page" : undefined}
                >
                    {i}
                </Pagination.Item>
            );
        }

        return items;
    }




    setProgramRefId() {
        const { messagesReceived, roles } = this.props;
        // Temporary users edit shared reports and have no lector participation in the admin service.
        if (roles.includes(TEMPORARY_USER)) {
            return;
        }
        rest.doGet("/admin/users/isLector").then(
            (json) => {
                const actions = [];
                if (json?.lector) {
                    actions.push(
                        {
                            labelKey: "respondents.overview.button.new.report",
                            onClick: () => this.setState({
                                showReportActivationModal: true,
                                lectorates: json.lectorates,
                                programRefId: json.programRefId
                            }),
                            icon: <FontAwesomeIcon icon={faPlus} className="me-2" />,
                        }
                    )
                }

                if (REPORT_STATUS_BUTTON_ROLES.some(rol => roles.includes(rol))) {
                    actions.push(
                        {
                            labelKey: PDF_REPORT_LABEL_KEY,
                            onClick: this.downloadPdfReport,
                            icon: <FontAwesomeIcon icon={faFilePdf} className="me-2" />,
                            hide: true
                        },
                        {
                            labelKey: REPORT_STATUS_LABEL_KEY,
                            onClick: this.downloadReporting,
                            icon: <FontAwesomeIcon icon={faFileCsv} className="me-2" />,
                            hide: true
                        }
                    )
                }

                this.setState({ actions });
            },
            (reason) => {
                messagesReceived(createBackendErrorMessages(reason));
            }
        );
    }

    downloadReporting() {
        const { downloadReportingStatus, filters } = this.props;
        downloadReportingStatus(filters);

    }

    downloadPdfReport() {
        this.setState({ showDownloadPDFDialog: true });
    }


    isValidRowToShow(row) {
        const { filters } = this.props;
        const periodStart = filters.periodStart;
        const periodEnd = filters.periodEnd;
        const status = filters.status;
        return (!periodStart.length || getFullYearFromString(row[roModel.start]) >= periodStart) &&
            (!periodEnd.length || getFullYearFromString(row[roModel.start]) <= periodEnd) &&
            (!status.length || row[roModel.status] === status);
    }

    getPeriodStartOptions(periodOptions) {
        const { filters } = this.props;
        const periodEnd = filters.periodEnd;
        return periodEnd.length ?
            periodOptions.filter(option => option <= periodEnd) :
            periodOptions;
    }

    getPeriodEndOptions(periodOptions) {
        const { filters } = this.props;
        const periodStart = filters.periodStart;
        return periodStart.length ?
            periodOptions.filter(option => option >= periodStart) :
            periodOptions;
    }

    render() {
        const {
            list,
            sortTableTemplateBy,
            sortTableTemplateAscending,
            intl,
            surveyName,
            lockByUser,
            lockFrom,
            showReadOnlyDialog,
            hideReadOnlyDialog,
            selectedProgram,
            showLecturer,
            filters
        } = this.props;

        const rowsAfterFilter = this.getFilteredRows(list, filters);
        const allRows = this.applySearch(rowsAfterFilter);
        const rows = this.getPagedRows(allRows);
        const statusOptions = list.map(row => row[roModel.status]);
        const periodOptions = list.map(row => getFullYearFromString(row[roModel.start]));
        const periodStartOptions = this.getPeriodStartOptions(periodOptions);
        const periodEndOptions = this.getPeriodEndOptions(periodOptions);

        const { searchQuery } = this.state;
        const actionsToShow = this.state.actions.map(action =>
            [REPORT_STATUS_LABEL_KEY, PDF_REPORT_LABEL_KEY].includes(action.labelKey) ?
                { ...action, hide: !SurveyUtils.isSemesterTypeProgram(selectedProgram?.overview?.name) } :
                action
        )

        const status = filters.status.length && statusOptions.includes(filters.status) ? filters.status : "";
        const periodStart = filters.periodStart.length && periodStartOptions.includes(Number(filters.periodStart)) ? filters.periodStart : "";
        const periodEnd = filters.periodEnd.length && periodEndOptions.includes(Number(filters.periodEnd)) ? filters.periodEnd : "";

        if (status !== filters.status || periodStart !== filters.periodStart || periodEnd !== filters.periodEnd) {
            this.updateFilter({
                ...filters,
                status: status,
                periodStart: periodStart,
                periodEnd: periodEnd
            });
        }

        return (
            <StandardView fluid id="respondentsOverview" titleKey="respondents.overview.pageTitle" actions={actionsToShow}>
                {showReadOnlyDialog && (
                    <LockInfoDialog
                        intl={intl}
                        surveyName={surveyName}
                        lockByUser={lockByUser}
                        lockFrom={lockFrom}
                        showReadOnlyDialog={showReadOnlyDialog}
                        hideReadOnlyDialog={hideReadOnlyDialog}
                    />
                )}

                {this.state.showReportActivationModal &&
                    <RespondentActivationContainer
                        close={() => this.setState({ showReportActivationModal: false })}
                        lectorates={this.state.lectorates}
                    />
                }

                <FundingAreaContainer
                    target="overview"
                    filterSelected={(fundingArea, program, programInstance, project) =>
                        this.loadOverviewListFilter(fundingArea, program, programInstance, project)
                    }
                />

                <ButtonToolbar className="mt-2">
                    <ButtonGroup className="gap-lg align-items-center">

                        <FormGroup className="m-0 flex-center gap-sm mb-1">
                            <FormattedMessage id="respondents.overview.reportPeriod" />
                            <FormSelect
                                className="d-inline-flex"
                                value={filters.periodStart}
                                onChange={(event) =>
                                    this.updateFilter({ ...filters, periodStart: event.target.value })
                                }

                            >
                                {renderOptions(
                                    periodStartOptions,
                                    "survey.editor.text.number.from",
                                    null,
                                    (option) => SurveyUtils.getReportPeriodLabel(intl, selectedProgram?.overview?.name, option)
                                )}
                            </FormSelect>
                        </FormGroup>

                        <FormGroup className="m-0 flex-center gap-sm mb-1">
                            <FormSelect
                                className="d-inline-flex"
                                value={filters.periodEnd}
                                onChange={(event) =>
                                    this.updateFilter({ ...filters, periodEnd: event.target.value })
                                }
                            >
                                {renderOptions(
                                    periodEndOptions,
                                    "survey.editor.text.number.till",
                                    null,
                                    (option) => SurveyUtils.getReportPeriodLabel(intl, selectedProgram?.overview?.name, option)
                                )}
                            </FormSelect>
                        </FormGroup>

                        <FormGroup className="m-0 flex-center gap-sm mb-1">
                            <FormSelect
                                className="d-inline-flex"
                                value={filters.status}
                                onChange={(event) =>
                                    this.updateFilter({ ...filters, status: event.target.value })
                                }
                            >
                                {renderOptions(statusOptions, "templateList.status", "respondents.detail.status.")}
                            </FormSelect>
                        </FormGroup>
                        <div className="m-0 flex-center gap-sm mb-1">
                            <input
                                type="search"
                                className="form-control w-fit"
                                value={searchQuery}
                                onChange={this.handleSearchChange}
                                placeholder={intl.formatMessage({
                                id: "respondents.overview.search.placeholder",
                                defaultMessage: "Suche in Berichterstattung..."
                                })}
                                aria-label={intl.formatMessage({
                                id: "respondents.overview.search.aria",
                                defaultMessage: "Search in overview"
                                })}
                            />
                        </div>
                    </ButtonGroup>

                </ButtonToolbar>
                {allRows.length > this.state.pageSize && (
                    <div className="d-flex justify-content-between align-items-center mt-3 ">
                    <span>
                        <FormattedMessage
                            id="pagination.showing"
                            values={{
                                current: this.state.currentPage,
                                total: Math.max(1, Math.ceil(allRows.length / this.state.pageSize)),
                            }}
                        />
                    </span>

                    <Pagination className="mb-0" size="sm">
                        <Pagination.First
                            onClick={() => this.goToPage(1)}
                            disabled={this.state.currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => this.goToPage(this.state.currentPage - 1)}
                            disabled={this.state.currentPage === 1}
                        >

                        </Pagination.Prev>

                        {this.renderPaginationItems(allRows.length)}

                        <Pagination.Next
                            onClick={() => this.goToPage(this.state.currentPage + 1)}
                            disabled={
                                this.state.currentPage >=
                                Math.ceil(allRows.length / this.state.pageSize)
                            }
                        >

                        </Pagination.Next>
                        <Pagination.Last
                            onClick={() => this.goToPage(Math.ceil(allRows.length / this.state.pageSize))}
                            disabled={
                                this.state.currentPage >=
                                Math.ceil(allRows.length / this.state.pageSize)
                            }
                        />
                    </Pagination>
                </div>)}


                <OverviewTable
                    rows={rows}
                    rowRenderer={this.row}
                    showLecturer={showLecturer}
                    sortTableTemplateBy={sortTableTemplateBy}
                    sortTableTemplateAscending={sortTableTemplateAscending}
                    onSortColumn={this.onSortColumn.bind(this)}
                />

                {allRows.length > this.state.pageSize && (<div className="d-flex justify-content-between align-items-center mt-3 ">
                    <span>
                        <FormattedMessage
                            id="pagination.showing"
                            values={{
                                current: this.state.currentPage,
                                total: Math.max(1, Math.ceil(allRows.length / this.state.pageSize)),
                            }}
                        />
                    </span>

                    <Pagination className="mb-0" size="sm">
                        <Pagination.First
                            onClick={() => this.goToPage(1)}
                            disabled={this.state.currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => this.goToPage(this.state.currentPage - 1)}
                            disabled={this.state.currentPage === 1}
                        >

                        </Pagination.Prev>

                        {this.renderPaginationItems(allRows.length)}

                        <Pagination.Next
                            onClick={() => this.goToPage(this.state.currentPage + 1)}
                            disabled={
                                this.state.currentPage >=
                                Math.ceil(allRows.length / this.state.pageSize)
                            }
                        >

                        </Pagination.Next>
                        <Pagination.Last
                            onClick={() => this.goToPage(Math.ceil(allRows.length / this.state.pageSize))}
                            disabled={
                                this.state.currentPage >=
                                Math.ceil(allRows.length / this.state.pageSize)
                            }
                        />
                    </Pagination>
                </div>)}



                {this.state.showDownloadPDFDialog &&
                    <DownloadPDFReports
                        show={this.state.showDownloadPDFDialog}
                        close={() => this.setState({ showDownloadPDFDialog: false })}
                        periodEnd={filters.periodEnd}
                        periodStart={filters.periodStart}
                        status={filters.status}
                        statusOptions={statusOptions}
                        periodEndOptions={periodEndOptions}
                        periodStartOptions={periodStartOptions}
                        {...this.props}
                        {...filters}
                    />
                }
            </StandardView>
        );
    }

    loadOverviewListFilter() {
        let { loadOverview } = this.props;
        // Reset to first page before reloading data
        this.setState({ currentPage: 1 }, () => {
            loadOverview();
        });
    }

    componentDidUpdate(prevProps) {
        const previousSearchQuery = prevProps.filters?.searchQuery || "";
        const searchQuery = this.props.filters?.searchQuery || "";
        if (previousSearchQuery !== searchQuery && searchQuery !== this.state.searchQuery) {
            this.setState({
                searchQuery,
                currentPage: 1
            });
        }
    }

    componentDidMount() {
        let { loadOverview } = this.props;
        this.setProgramRefId();
        //We are triggering 'ducks' function, that will make a rest call and get data from the server.
        loadOverview();
    }

    /**
     * A row renderer
     */
    row(roRow, i) {
        const { intl, loadDetails, sortTableTemplateBy, showDialog, deleteReport, showLecturer } = this.props;
        const tooltip = roRow[roModel.lastEdit] && (
            <Tooltip className="date-tooltip overview-date-tooltip" id="tooltip">
                <FormattedTime value={new Date(roRow[roModel.lastEdit])} hour="numeric" minute="2-digit" second="2-digit" />
            </Tooltip>
        );

        return (
            <tr key={roRow[roModel.reportPeriodGid] || roRow[roModel.projectForeignId] || i}>
                <td className={SurveyUtils.addClassForTableColumns(roModel.program, sortTableTemplateBy)}>
                    {roRow[roModel.program]}
                </td>
                <td className={SurveyUtils.addClassForTableColumns(roModel.title, sortTableTemplateBy)} title={roRow[roModel.title]}>
                    {roRow[roModel.title]}
                </td>
                <td className={SurveyUtils.addClassForTableColumns(roModel.projectForeignId, sortTableTemplateBy)}>
                    {SurveyUtils.hasProjectForeignId(roRow) ? (
                        roRow[roModel.projectForeignId]
                    ) : (
                        <FormattedMessage id="respondents.overview.project.id.do.not.exist" />
                    )}
                </td>
                <td
                    className={SurveyUtils.addClassForTableColumns(roModel.universityName, sortTableTemplateBy)}
                    title={roRow[roModel.universityName]}
                >
                    {roRow[roModel.universityName]}
                </td>
                <td
                    className={SurveyUtils.addClassForTableColumns(roModel.project, sortTableTemplateBy)}
                    title={roRow[roModel.project]}
                >
                    {roRow[roModel.project]}
                </td>
                {showLecturer && (
                    <td
                        className={SurveyUtils.addClassForTableColumns(roModel.lecturer, sortTableTemplateBy)}
                        title={roRow[roModel.lecturer]}
                    >
                        {roRow[roModel.lecturer]}
                    </td>
                )}
                <td className={SurveyUtils.addClassForTableColumns(roModel.start, sortTableTemplateBy, "date")}>
                    {SurveyUtils.getReportPeriodLabel(intl, roRow[roModel.program], getFullYearFromString(roRow[roModel.start]))}
                </td>
                <td className={SurveyUtils.addClassForTableColumns(roModel.status, sortTableTemplateBy)}>
                    <FormattedMessage id={`respondents.detail.status.${roRow[roModel.status]}`} />
                </td>
                <td className={SurveyUtils.addClassForTableColumns(roModel.lastEdit, sortTableTemplateBy, "date")}>
                    {roRow[roModel.lastEdit] && (
                        <OverlayTrigger placement="right" overlay={tooltip}>
                            <span>
                                <FormattedDate value={new Date(roRow[roModel.lastEdit])} year="numeric" month="2-digit" day="2-digit" />
                            </span>
                        </OverlayTrigger>
                    )}
                </td>
                <td className="actions">
                    <ButtonGroup>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => this.onSurveyPreview(roRow)}
                            title={intl.formatMessage({ id: "respondents.overview.tooltip.edit" })}
                        >
                            <FontAwesomeIcon icon={faPlayCircle} />
                        </Button>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => loadDetails(roRow)}
                            title={intl.formatMessage({ id: "respondents.overview.tooltip.status" })}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                        </Button>
                        {SurveyUtils.isSemesterTypeProgram(roRow[roModel.program]) &&
                            <TrashButton
                                disabled={roRow[roModel.status] === SENT_REPORT_STATUS}
                                title={intl.formatMessage({ id: "app.common.action.delete" })}
                                onClick={() => {
                                    showDialog(
                                        roRow[roModel.reportPeriodGid],
                                        deleteReport,
                                        "dialog.respondents.overview.delete.title",
                                        "dialog.respondents.overview.delete.text",
                                        "dialog.delete",
                                        "dialog.dismiss",
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                                    )
                                }}
                            />
                        }
                    </ButtonGroup>
                </td>
            </tr>
        );
    }

    onSortColumn(columnType) {
        const { sortTableTemplateAscending, sortColumn, sortTableTemplateBy } = this.props;
        //triggers sorting.
        // Reset pagination when sort order or column changes
        this.setState({ currentPage: 1 }, () => {
            if (sortTableTemplateBy === columnType) {
                sortColumn(columnType, !sortTableTemplateAscending, LIST);
            } else {
                sortColumn(columnType, true, LIST);
            }
        });
    }

    onSurveyPreview(roRow) {
        const { propertyChange, lockInfoOfSurvey, overviewPropertyChange, isAdminUser, allowAdminEditing } = this.props;
        propertyChange("surveyCurrentPage", "");
        if (SurveyUtils.hasProjectForeignId(roRow)) {
            overviewPropertyChange("projectForeignId", roRow[roModel.projectForeignId]);
        } else {
            overviewPropertyChange("projectForeignId", null);
        }
        lockInfoOfSurvey(roRow, "", isAdminUser, allowAdminEditing);
    }
}

RespondentsOverview.propTypes = {
    showLecturer: PropTypes.bool,
    list: PropTypes.array,
    loadOverview: PropTypes.func,
    sortColumn: PropTypes.func,
    loadDetails: PropTypes.func,
    propertyChange: PropTypes.func,
    sortTableTemplateBy: PropTypes.string,
    lockInfoOfSurvey: PropTypes.func,
    sortTableTemplateAscending: PropTypes.bool,
    isAdminUser: PropTypes.bool,
    intl: PropTypes.object,
    surveyName: PropTypes.string,
    lockFrom: PropTypes.number,
    lockByUser: PropTypes.string,
    showReadOnlyDialog: PropTypes.bool,
    hideReadOnlyDialog: PropTypes.func,
    overviewPropertyChange: PropTypes.func,
    messagesReceived: PropTypes.func,
    roles: PropTypes.arrayOf(PropTypes.string),
};

export default injectIntl(RespondentsOverview);
